import PVRecCategoryCodes from '@constants/pvrecCategoryCodes';
import constants from '@containers/widget/constants';
import { useAppSelector } from '@redux/hooks';
import { selectLoggedInStatus } from '@redux/membership/selectors';
import { setMaxResultColumns } from '@redux/search';
import {
	type VendorToFindSimilar,
	fetchPaiverSuccess,
} from '@redux/widget/actions';
import { internalPvRecRecommendationsUrl } from '@settings';
import { useEffect } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';

type PvRecProxySource =
	| 'paiver'
	| 'paiver anon'
	| 'paiver ros'
	| 'paiver ros anon'
	| 'similarity'
	| 'similarity ros';

interface PvRecProxyResponse {
	profiles: Vendor.Raw[];
	source: PvRecProxySource;
	hasActivityRecommendations?: boolean;
	activityRecommendation?: VendorToFindSimilar | null;
	shouldHideRightRail: boolean;
}

/**
 * Client-side hook to fetch pvRec recommendations for right rail.
 * Respects Paiver experiment variant (V1 vs V2) and member authentication status.
 */
export function useFetchRecommendations(): void {
	const dispatch = useDispatch();
	const {
		categoryCode,
		city,
		stateCode,
		anonymousId,
		marketCode,
		experiments,
		isMobile,
		isTablet,
		strategy,
		isLoggedIn,
	} = useAppSelector(
		(state) => ({
			categoryCode: state.category?.code,
			city: state.location.city,
			stateCode: state.location.stateCode,
			anonymousId: state.settings.anonymousId,
			marketCode: state.settings.marketCode,
			experiments: state.experiments,
			isMobile: state.viewport.isMobile,
			isTablet: state.viewport.isTablet,
			strategy: 'similarUser',
			isLoggedIn: selectLoggedInStatus(state),
		}),
		shallowEqual,
	);
	const loginStateDependency = isLoggedIn;

	// biome-ignore lint/correctness/useExhaustiveDependencies: Re-fetch on login changes because activity-based and Paiver recommendations are member-aware
	useEffect(() => {
		// Skip if viewport is not desktop
		const isDesktop = !(isMobile || isTablet);
		if (!isDesktop) {
			return;
		}

		// Skip if category not supported
		if (!categoryCode || !PVRecCategoryCodes.includes(categoryCode)) {
			return;
		}

		if (!city || !stateCode || !marketCode) {
			return;
		}

		dispatch({ type: constants.CLEAR_RECOMMENDED_VENDORS });

		const fetchAndDispatch = async () => {
			try {
				const response = await fetch(internalPvRecRecommendationsUrl, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						categoryCode,
						city,
						stateCode,
						marketCode,
						anonymousId,
						experiments,
						strategy,
					}),
				});

				if (!response.ok) {
					throw new Error(`HTTP status ${response.status}`);
				}

				const proxyResponse = (await response.json()) as PvRecProxyResponse;

				if (proxyResponse.profiles.length > 0) {
					if (
						proxyResponse.hasActivityRecommendations &&
						proxyResponse.activityRecommendation
					) {
						dispatch({
							type: constants.FETCH_SIMILAR_VENDORS_BASED_ON_ACTIVITY_SUCCESS,
							vendorToFindSimilar: proxyResponse.activityRecommendation,
							payload: {
								data: {
									similarity: {
										profiles: proxyResponse.profiles,
									},
								},
							},
						});
					} else {
						dispatch(
							fetchPaiverSuccess({
								profiles: proxyResponse.profiles,
								source: proxyResponse.source,
							}),
						);
					}
				} else {
					dispatch(setMaxResultColumns(4));
				}
			} catch (_error) {
				// On error, hide right-rail
				dispatch(setMaxResultColumns(4));
			}
		};

		fetchAndDispatch();
	}, [
		dispatch,
		categoryCode,
		city,
		stateCode,
		anonymousId,
		marketCode,
		experiments,
		strategy,
		isMobile,
		isTablet,
		loginStateDependency,
	]);
}
